import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface RichTextEditorProps {
  /** HTML string. */
  value: string;
  onChange: (html: string) => void;
  /**
   * Character budget. Enforced, not advisory: typing and pasting stop at the
   * cap. Counts visible text, so markup and formatting are free.
   */
  maxLength?: number;
  placeholder?: string;
  className?: string;
}

// One definition of "how long is this", so the counter and the cap can never
// disagree about what they are measuring. Module scope: it captures nothing,
// and at render scope the useCallback([]) hooks below would close over a stale
// copy of it.
const measure = (text: string) => text.replace(/\s+/g, ' ').trim().length;

const selectClass =
  'appearance-none bg-transparent pr-4 text-base md:text-[13px] text-gray-600 focus:outline-none cursor-pointer';

const FONT_SIZES = ['12px', '14px', '16px', '18px'];

const BLOCK_FORMATS: Array<{ value: string; label: string }> = [
  { value: 'h2', label: 'Header' },
  { value: 'h3', label: 'Subheader' },
  { value: 'p', label: 'Normal' },
];

const ALIGNMENTS: Array<{ command: string; label: string }> = [
  { command: 'justifyLeft', label: 'Left' },
  { command: 'justifyCenter', label: 'Center' },
  { command: 'justifyRight', label: 'Right' },
  { command: 'justifyFull', label: 'Justify' },
];

/**
 * Lightweight rich text editor matching the Dash form designs: a fixed
 * toolbar over a contenteditable body with a character counter. Built on
 * document.execCommand so it needs no editor dependency.
 */
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  maxLength = 1000,
  placeholder,
  className = '',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [blockFormat, setBlockFormat] = useState('h2');
  const [fontSize, setFontSize] = useState('14px');

  // Only write into the DOM when the incoming value diverges, so typing
  // doesn't reset the caret on every keystroke.
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value;
    }
  }, [value]);

  const emit = useCallback(() => {
    onChange(editorRef.current?.innerHTML ?? '');
  }, [onChange]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, arg);
      emit();
    },
    [emit]
  );

  const handleLink = useCallback(() => {
    const url = window.prompt('Enter a URL');
    if (url) exec('createLink', url);
  }, [exec]);

  // Counter reflects visible text, not markup.
  const textLength = measure(
    value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ')
  );

  /**
   * Live length straight from the DOM. The `value` prop lags a render behind
   * during fast typing, so a guard measuring it would let a burst overshoot.
   */
  const currentLength = useCallback(
    () => measure(editorRef.current?.innerText ?? ''),
    []
  );

  /**
   * What the selection would give back if replaced — but only a selection
   * inside this editor. `window.getSelection()` is global, so a selection in
   * some other field used to hand this one a phantom budget.
   */
  const selectionLength = useCallback(() => {
    const selection = window.getSelection();
    const el = editorRef.current;
    if (!selection || !el || selection.isCollapsed) return 0;
    if (
      !el.contains(selection.anchorNode) ||
      !el.contains(selection.focusNode)
    ) {
      return 0;
    }
    return measure(selection.toString());
  }, []);

  /**
   * Hard cap on input. The counter alone let text run past the budget and only
   * turned red about it, so an over-long block still reached the API.
   *
   * This listens to the NATIVE `beforeinput`, not React's synthetic one:
   * React polyfills that event from `textInput`/`keypress`, and the polyfilled
   * event carries no `inputType` — which silently made every branch below
   * unreachable and let drag-drop through untouched.
   *
   * Deletions, undo/redo and pure formatting never add characters, so they
   * pass; otherwise a full editor could not be emptied or re-styled.
   */
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    const guard = (event: InputEvent) => {
      const inputType = event.inputType ?? '';
      if (
        inputType.startsWith('delete') ||
        inputType.startsWith('history') ||
        inputType.startsWith('format')
      ) {
        return;
      }

      // `data` is null for pasted and dropped input, where the payload lives
      // on the transfer instead. Anything else adds at least one character.
      const incoming =
        event.data?.length ??
        event.dataTransfer?.getData('text/plain').length ??
        1;

      if (currentLength() - selectionLength() + incoming > maxLength) {
        event.preventDefault();
      }
    };

    el.addEventListener('beforeinput', guard);
    return () => el.removeEventListener('beforeinput', guard);
  }, [maxLength, currentLength, selectionLength]);

  /**
   * A paste that fits keeps the native behaviour (and its formatting); one
   * that overflows is truncated to the remaining budget as plain text rather
   * than rejected outright, which is the less annoying half-measure.
   */
  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const plain = event.clipboardData.getData('text/plain') ?? '';
    const budget = maxLength - (currentLength() - selectionLength());
    if (plain.length <= budget) return;

    event.preventDefault();
    if (budget > 0) {
      document.execCommand('insertText', false, plain.slice(0, budget));
      emit();
    }
  };

  const toolbarButton = (
    icon: string,
    command: string,
    label: string
  ): React.ReactElement => (
    <button
      key={command}
      type="button"
      title={label}
      aria-label={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => exec(command)}
      className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    >
      <i className={`${icon} text-[15px]`}></i>
    </button>
  );

  const divider = <span className="w-px h-5 bg-gray-200 mx-1" />;

  return (
    <div
      className={`border border-gray-200 rounded-lg overflow-hidden ${className}`.trim()}
    >
      <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-200 bg-white flex-wrap">
        <div className="relative flex items-center">
          <select
            value={blockFormat}
            onChange={(e) => {
              setBlockFormat(e.target.value);
              exec('formatBlock', e.target.value);
            }}
            className={selectClass}
            aria-label="Text style"
          >
            {BLOCK_FORMATS.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line text-[14px] text-gray-400 -ml-3 pointer-events-none"></i>
        </div>

        {divider}

        <div className="relative flex items-center">
          <select
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            className={selectClass}
            aria-label="Font size"
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line text-[14px] text-gray-400 -ml-3 pointer-events-none"></i>
        </div>

        {divider}

        {toolbarButton('ri-bold', 'bold', 'Bold')}
        {toolbarButton('ri-italic', 'italic', 'Italic')}
        {toolbarButton('ri-underline', 'underline', 'Underline')}
        {toolbarButton('ri-strikethrough', 'strikeThrough', 'Strikethrough')}

        {divider}

        <div className="relative flex items-center rounded hover:bg-gray-100">
          <i className="ri-align-left text-[15px] text-gray-500 ml-1.5 pointer-events-none"></i>
          <i className="ri-arrow-down-s-line text-[14px] text-gray-400 mr-1 pointer-events-none"></i>
          <select
            onChange={(e) => exec(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            aria-label="Alignment"
            defaultValue=""
          >
            <option value="" disabled>
              Align
            </option>
            {ALIGNMENTS.map((alignment) => (
              <option key={alignment.command} value={alignment.command}>
                {alignment.label}
              </option>
            ))}
          </select>
        </div>

        {divider}

        <button
          type="button"
          disabled
          title="Comments — coming soon"
          className="w-7 h-7 flex items-center justify-center rounded text-gray-300 cursor-not-allowed"
        >
          <i className="ri-chat-1-line text-[15px]"></i>
        </button>
        <button
          type="button"
          title="Insert link"
          aria-label="Insert link"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleLink}
          className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <i className="ri-link text-[15px]"></i>
        </button>
        <button
          type="button"
          disabled
          title="Mentions — coming soon"
          className="w-7 h-7 flex items-center justify-center rounded text-gray-300 cursor-not-allowed"
        >
          <i className="ri-at-line text-[15px]"></i>
        </button>
      </div>

      <div className="relative bg-white">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onPaste={handlePaste}
          onInput={emit}
          onBlur={emit}
          data-placeholder={placeholder}
          style={{ fontSize }}
          className="min-h-[150px] max-h-[400px] overflow-y-auto resize-y px-4 py-3 text-gray-700 leading-[22px] focus:outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-blue-600 [&_a]:underline empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        />
        {/* Over-limit is still possible for content saved before the cap
            existed, so the red state stays meaningful; at-limit is amber to
            signal "no room left" without reading as an error. */}
        <span
          className={`absolute bottom-2 right-3 text-[12px] ${
            textLength > maxLength
              ? 'text-red-500'
              : textLength === maxLength
                ? 'text-amber-600'
                : 'text-gray-400'
          }`}
        >
          {textLength}/{maxLength}
        </span>
      </div>
    </div>
  );
};

export default RichTextEditor;
